import { LooseKeys, UseFormReturnType, schemaResolver, useForm } from '@mantine/form';
import { FormEvent, ReactElement, ReactNode, Ref, useEffect, useMemo } from 'react';
import { ZodRawShape, z } from 'zod';

import useFormRef from '@/hooks/useFormRef.ts';

export interface Props<T> {
    initialValues?: T;
    handleSubmit: (values: T, event: FormEvent<HTMLFormElement> | undefined) => void;
    children: (form: UseFormReturnType<T>, isEdit: boolean) => ReactNode;
    schema?: z.ZodObject<ZodRawShape> | z.ZodSchema<z.ZodObject<ZodRawShape>>;
    className?: string;
    onValuesChange?: (values: T) => void;
    validateInputOnChange?: boolean | LooseKeys<T>[];
    validateInputOnBlur?: boolean | LooseKeys<T>[];
    checkIfValid?: (is_valid: boolean) => void;
    isEdit?: boolean;
}

const Form = <T extends Record<string, unknown>>({
    initialValues,
    handleSubmit,
    children,
    schema,
    className,
    onValuesChange,
    validateInputOnChange = false,
    validateInputOnBlur = false,
    checkIfValid,
    isEdit = false,
}: Props<T>): ReactElement => {
    const form = useForm<T>({
        mode: 'uncontrolled',
        initialValues: initialValues,
        validateInputOnChange: validateInputOnChange,
        validateInputOnBlur: validateInputOnBlur,
        validate: schema ? schemaResolver(schema, { sync: true }) : undefined,
        onValuesChange: onValuesChange,
    });
    const ref = useFormRef();

    const isValid = useMemo(() => form.isValid(), [form]);

    useEffect(() => {
        if (!checkIfValid) return;

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
        checkIfValid(isValid);
    }, [isValid]);

    return (
        <form ref={ref as Ref<HTMLFormElement>} onSubmit={form.onSubmit(handleSubmit)} className={className}>
            {children(form, isEdit)}
        </form>
    );
};

export default Form;
